import { createContext, useContext } from "react";

import type { KnobContextValue } from "./Knob.types";
import { KNOB_SIZES } from "./constants";

const noopControlRef: KnobContextValue["controlRef"] = { current: null };

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

type KnobContextInput = Omit<KnobContextValue, "percent" | "radius">;

export function makeKnobContext({
  value = 0,
  min = 0,
  max = 100,
  step = 1,
  disabled = false,
  labelId = "",
  diameter = KNOB_SIZES.md.diameter,
  strokeWidth = KNOB_SIZES.md.strokeWidth,
  thumbSize = KNOB_SIZES.md.thumbSize,
  boxSize = `${KNOB_SIZES.md.diameter}px`,
  controlRef = noopControlRef,
  setValue = () => {},
}: Partial<KnobContextInput>): KnobContextValue {
  const percent = max > min ? clamp((value - min) / (max - min), 0, 1) : 0;
  const radius = (diameter - strokeWidth) / 2;

  return {
    value,
    min,
    max,
    step,
    disabled,
    labelId,
    diameter,
    radius,
    strokeWidth,
    thumbSize,
    boxSize,
    controlRef,
    setValue,
    percent,
  };
}

export const KnobContext = createContext<KnobContextValue>(makeKnobContext({}));

export function useKnobContext(): KnobContextValue {
  return useContext(KnobContext);
}
